import { PublicClientApplication, InteractionType } from '@azure/msal-browser'
import { msalConfig, loginRequest } from './authConfig'
import { persistedLog } from '../core/utils/devLogger'

export const msalInstance = new PublicClientApplication(msalConfig)

let msalInitStarted = false
let msalInitPromise = null

function saveToSession(key, value) {
	try {
		sessionStorage.setItem(key, JSON.stringify(value))
	} catch (_err) {
		// no-op
	}
}

function saveToLocal(key, value) {
	try {
		if (typeof value === 'string') {
			localStorage.setItem(key, value)
		} else {
			localStorage.setItem(key, JSON.stringify(value))
		}
	} catch (_err) {
		// no-op
	}
}

function persistTokenResultToLocalStorage(tokenResult, account) {
	try {
		if (!tokenResult) return

		// API calls must use the API access token only — never fall back to the ID token (PRD FR-002).
		const effectiveToken = tokenResult.accessToken;

		if (effectiveToken) {
			// Maintain legacy key expected by existing code paths
			saveToLocal('Token', effectiveToken)
		}
		if (tokenResult.idToken) {
			saveToLocal('MSAL_IdToken', tokenResult.idToken)
		}

		// Ensure LoginInfo is set for isAuthenticated checks
		if (effectiveToken && account) {
			const idc = tokenResult.idTokenClaims || {}
			const userRoles = Array.isArray(idc.roles) ? idc.roles : []
			const loginInfo = {
				user: {
					firstName: account.name || 'User',
					lastName: '',
					id: account.homeAccountId,
					role: userRoles[0] || '',
					company: ''
				},
				token: effectiveToken
			}
			saveToLocal('LoginInfo', loginInfo)

			if (idc.name) saveToLocal('UserName', idc.name)
			if (idc.preferred_username) saveToLocal('UserEmail', idc.preferred_username)

			console.log('persistTokenResultToLocalStorage: LoginInfo saved', loginInfo);
		}

		const summary = {
			token_type: 'Bearer',
			scopes: tokenResult.scopes,
			expires_on_iso: tokenResult.expiresOn ? tokenResult.expiresOn.toISOString() : undefined,
			tenantId: tokenResult.tenantId,
			authority: tokenResult.authority,
			account: account
				? {
					username: account.username,
					homeAccountId: account.homeAccountId,
					environment: account.environment,
					localAccountId: account.localAccountId,
				}
				: undefined,
		}
		saveToLocal('MSAL_Token_Summary', summary)
	} catch (_err) {
		// no-op
	}
}

function logMsalSuccess(context, details) {
	try {
		persistedLog(`MSAL ${context} success`, details, 'log')
	} catch (_err) {
		// no-op
	}
}

function logMsalError(context, error) {
	try {
		const structured =
			error && typeof error === 'object'
				? {
					name: error.name,
					message: error.message,
					errorCode: error.errorCode,
					subError: error.subError,
				}
				: error
		persistedLog(`MSAL ${context} error`, structured, 'error')
	} catch (_err) {
		// no-op
	}
}

function logAllClaims(claims) {
	if (!claims) {
		console.log('MSAL idTokenClaims: <none>')
		return
	}
	console.log('MSAL idTokenClaims (object):', claims)
	try {
		// persist last claims for viewing after redirects
		try {
			sessionStorage.setItem('MSAL_LAST_IDTOKEN_CLAIMS', JSON.stringify(claims))
			persistedLog('IDTokenClaims saved', { keys: Object.keys(claims || {}) })
		} catch (_e) { }
		Object.entries(claims).forEach(([key, value]) => {
			console.log(`MSAL claim ${key}:`, value)
		})
	} catch (_err) {
		// No-op for logging issues
	}
}

// One-time config log
try {
	console.log('MSAL config:', {
		clientId: msalConfig?.auth?.clientId,
		authority: msalConfig?.auth?.authority,
		redirectUri: msalConfig?.auth?.redirectUri,
		cacheLocation: msalConfig?.cache?.cacheLocation,
		storeAuthStateInCookie: msalConfig?.cache?.storeAuthStateInCookie,
	})
} catch (_err) {
	// no-op
}

async function ensureMsalInitialized() {
	if (msalInitStarted) {
		logMsalSuccess('ensureMsalInitialized', 'already started; returning existing promise')
		return msalInitPromise
	}
	logMsalSuccess('ensureMsalInitialized', 'initializing MSAL instance')
	msalInitStarted = true
	msalInitPromise = (async () => {
		await msalInstance.initialize()
		logMsalSuccess('initialize', { redirectUri: msalConfig?.auth?.redirectUri })

		let result = null;
		try {
			result = await msalInstance.handleRedirectPromise()
		} catch (err) {
			logMsalError('handleRedirectPromise error in msal.js', err);
			sessionStorage.setItem('MSAL_INIT_ERROR', err?.message || err?.toString() || '');
		}

		if (result && result.account) {
			msalInstance.setActiveAccount(result.account)
			logAllClaims(result.idTokenClaims)
			logMsalSuccess('Chemplast1 Result', JSON.stringify(result))
			logMsalSuccess('handleRedirectPromise', {
				username: result.account.username,
				homeAccountId: result.account.homeAccountId,
				scopes: result.scopes,
			})
			// persist accounts info
			try {
				const accountsNow = msalInstance.getAllAccounts()
				saveToSession('MSAL_ACCOUNTS', accountsNow)
				saveToSession('MSAL_ACTIVE_ACCOUNT', result.account)
				saveToSession('MSAL_LAST_TOKEN_INFO', {
					scopes: result.scopes,
					expiresOn: result.expiresOn,
					tokenType: result.tokenType,
					tenantId: result.tenantId,
					authority: result.authority,
					account: {
						username: result.account.username,
						homeAccountId: result.account.homeAccountId,
						environment: result.account.environment,
						localAccountId: result.account.localAccountId,
					},
					accessTokenPreview: result.accessToken ? `${result.accessToken.slice(0, 10)}... (len=${result.accessToken.length})` : undefined,
				})
				// also persist to localStorage for app-wide usage
				persistTokenResultToLocalStorage(result, result.account)
			} catch (_e) { }
			return
		}
		logMsalSuccess('handleRedirectPromise', 'no result / no account to set active')
		const accounts = msalInstance.getAllAccounts()
		logMsalSuccess('accountsAfterInit', {
			count: accounts?.length || 0,
			firstUsername: accounts && accounts[0] ? accounts[0].username : undefined,
		})
		saveToSession('MSAL_ACCOUNTS', accounts || [])
		if (accounts && accounts.length > 0) {
			msalInstance.setActiveAccount(accounts[0])
			saveToSession('MSAL_ACTIVE_ACCOUNT', accounts[0])
			try {
				logMsalSuccess('acquireTokenSilent(init):attempt', {
					username: accounts[0].username,
					scopes: loginRequest?.scopes,
				})
				const tokenResult = await msalInstance.acquireTokenSilent({
					...loginRequest,
					account: accounts[0],
				})
				logAllClaims(tokenResult?.idTokenClaims)
				logMsalSuccess('acquireTokenSilent(init)', {
					username: accounts[0].username,
					expiresOn: tokenResult?.expiresOn,
					scopes: tokenResult?.scopes,
					accessTokenPreview: tokenResult?.accessToken
						? `${tokenResult.accessToken.slice(0, 10)}... (len=${tokenResult.accessToken.length})`
						: undefined,
				})
				// persist token metadata
				saveToSession('MSAL_LAST_TOKEN_INFO', {
					scopes: tokenResult?.scopes,
					expiresOn: tokenResult?.expiresOn,
					tokenType: tokenResult?.tokenType,
					tenantId: tokenResult?.tenantId,
					authority: tokenResult?.authority,
					account: {
						username: accounts[0].username,
						homeAccountId: accounts[0].homeAccountId,
						environment: accounts[0].environment,
						localAccountId: accounts[0].localAccountId,
					},
					accessTokenPreview: tokenResult?.accessToken
						? `${tokenResult.accessToken.slice(0, 10)}... (len=${tokenResult.accessToken.length})`
						: undefined,
				})
				// also persist to localStorage for app-wide usage
				persistTokenResultToLocalStorage(tokenResult, accounts[0])
			} catch (_err) {
				logMsalError('acquireTokenSilent(init)', _err)
			}
			return
		} else {
			logMsalSuccess('accountsAfterInit', 'no accounts found on init')
		}

		// No accounts found, initiate login redirect
		logMsalSuccess('No accounts found, initiating loginRedirect')
		msalInstance.loginRedirect(loginRequest)
	})()
	return msalInitPromise
}

export async function getMsalAuthHeader() {
	try {
		logMsalSuccess('getMsalAuthHeader', {
			locationHref: typeof window !== 'undefined' ? window.location.href : '<no-window>',
		})
		await ensureMsalInitialized()
		const accounts = msalInstance.getAllAccounts()
		if (!accounts || accounts.length === 0) {
			logMsalError('getMsalAuthHeader', 'no accounts found')
			return {}
		}
		const activeAccount = accounts[0]
		saveToSession('MSAL_ACCOUNTS', accounts)
		saveToSession('MSAL_ACTIVE_ACCOUNT', activeAccount)
		logMsalSuccess('acquireTokenSilent:attempt', {
			username: activeAccount.username,
			scopes: loginRequest?.scopes,
		})
		const result = await msalInstance.acquireTokenSilent({
			...loginRequest,
			account: activeAccount,
		})
		if (result?.accessToken) {
			logMsalSuccess('acquireTokenSilent', {
				username: activeAccount.username,
				expiresOn: result.expiresOn,
				scopes: result.scopes,
				accessTokenPreview: `${result.accessToken.slice(0, 10)}... (len=${result.accessToken.length})`,
			})
			// persist token metadata
			saveToSession('MSAL_LAST_TOKEN_INFO', {
				scopes: result.scopes,
				expiresOn: result.expiresOn,
				tokenType: result.tokenType,
				tenantId: result.tenantId,
				authority: result.authority,
				account: {
					username: activeAccount.username,
					homeAccountId: activeAccount.homeAccountId,
					environment: activeAccount.environment,
					localAccountId: activeAccount.localAccountId,
				},
				accessTokenPreview: `${result.accessToken.slice(0, 10)}... (len=${result.accessToken.length})`,
			})
			// also persist to localStorage for app-wide usage
			persistTokenResultToLocalStorage(result, activeAccount)
			return { Authorization: `Bearer ${result.accessToken}` }
		}
		logMsalError('acquireTokenSilent', 'no access token returned')
		return {}
	} catch (_err) {
		logMsalError('getMsalAuthHeader', _err)
		return {}
	}
}

/**
 * Returns the raw MSAL access token (no "Bearer " prefix) or null.
 * Used by SignalR's accessTokenFactory and the notification REST calls.
 */
export async function getMsalAccessToken() {
	try {
		await ensureMsalInitialized()
		const accounts = msalInstance.getAllAccounts()
		if (!accounts || accounts.length === 0) return null
		const result = await msalInstance.acquireTokenSilent({
			...loginRequest,
			account: accounts[0],
		})
		return result?.accessToken || null
	} catch (_err) {
		logMsalError('getMsalAccessToken', _err)
		return null
	}
}

/**
 * Current signed-in user's Entra object id (oid) from token claims, or null.
 */
export function getCurrentUserOid() {
	try {
		const accounts = msalInstance.getAllAccounts()
		const claims = accounts && accounts[0] ? accounts[0].idTokenClaims : null
		if (claims && (claims.oid || claims.sub)) {
			return claims.oid || claims.sub
		}
		// Fallback: MSAL localAccountId is the oid for single-tenant tokens.
		if (accounts && accounts[0] && accounts[0].localAccountId) {
			return accounts[0].localAccountId
		}
	} catch (_err) {
		// no-op
	}
	return null
}

export const msalGuardConfig = {
	interactionType: InteractionType.Redirect,
	authRequest: loginRequest,
}

export async function initializeMsal() {
	return ensureMsalInitialized()
}

export { loginRequest }

